import { z } from 'zod';

// ── Full education entry (server → client) ────────────────────────────────────
// Represents a single degree / credential in the candidate's education history.

export const EducationSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  institution: z.string(),
  degreeAndMajor: z.string(),
  graduationDate: z.string().nullable(),   // YYYY-MM-DD; null = in progress / unknown
  sortOrder: z.number().int(),             // user-defined display order
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// ── Create request (client → server) ─────────────────────────────────────────

export const CreateEducationSchema = z.object({
  institution: z.string().min(1, 'Institution is required').max(300).trim(),
  degreeAndMajor: z.string().min(1, 'Degree and major is required').max(300).trim(),
  graduationDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .nullable()
    .optional(),
});

// ── Update request — all fields optional ─────────────────────────────────────

export const UpdateEducationSchema = z.object({
  institution: z.string().min(1).max(300).trim().optional(),
  degreeAndMajor: z.string().min(1).max(300).trim().optional(),
  graduationDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .nullable()
    .optional(),
});

// ── Reorder request ───────────────────────────────────────────────────────────

export const ReorderEducationItemSchema = z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int(),
});

export const ReorderEducationSchema = z.object({
  reorders: z.array(ReorderEducationItemSchema).min(1),
});

// ── Resume document education entry ──────────────────────────────────────────
// Lightweight snapshot stored inside ResumeDocument.sections — carries only
// what's needed to render the education section on a resume.

export const ResumeEducationItemSchema = z.object({
  id: z.string().uuid(),
  institution: z.string(),
  degreeAndMajor: z.string(),
  graduationDate: z.string().nullable(),   // YYYY-MM-DD or null
});

// ── Derived types ─────────────────────────────────────────────────────────────

export type Education = z.infer<typeof EducationSchema>;
export type CreateEducation = z.infer<typeof CreateEducationSchema>;
export type UpdateEducation = z.infer<typeof UpdateEducationSchema>;
export type ReorderEducationItem = z.infer<typeof ReorderEducationItemSchema>;
export type ReorderEducation = z.infer<typeof ReorderEducationSchema>;
export type ResumeEducationItem = z.infer<typeof ResumeEducationItemSchema>;
